// ccp/src/lib/authRetry.js
//
// D40 (2026-09-16): the pure half of the console's token-refresh behaviour,
// kept out of auth/cognito.js so scripts/verify-auth-refresh.mjs can prove it
// (cognito.js reads import.meta.env at module scope and cannot be imported
// outside Vite).
//
// Two rules, both learned from a live 401 on an outbound dial:
//
// 1. SINGLE FLIGHT. The ID token lives 60 minutes. When it lapses, every API
//    caller races to refresh independently — the callbacks poll (30 s), the
//    Phase 3 transcript poller (3 s during a call), copilot, coaching,
//    wrap-up, and the dial itself. Concurrent refresh_token calls are a
//    problem on any pool that rotates refresh tokens: the first wins and the
//    rest are rejected. One shared in-flight promise removes the race.
//
// 2. ONLY A DEFINITIVE REJECTION ENDS THE SESSION. The old code called
//    save(null) on ANY failure, so one transient error — a dropped request,
//    a 5xx, a rotated-token loser — destroyed a session that was otherwise
//    fine, and every later call went out with no Authorization header (a 401
//    at the gateway, before any Lambda). A transient failure must leave the
//    stored session alone so the next call can retry.

// OAuth 2.0 token-endpoint errors that mean the refresh token itself is no
// longer usable. Anything else is treated as transient and retryable.
// (cognito.js's tokenRequest throws `new Error(data.error || 'token endpoint
// HTTP <status>')`, so the OAuth error code is the message.)
export const DEFINITIVE_AUTH_ERRORS = Object.freeze([
  'invalid_grant',        // refresh token expired, revoked, or already rotated away
  'invalid_client',
  'unauthorized_client',
  'invalid_request',      // malformed grant — retrying cannot help
]);

export function isDefinitiveAuthFailure(err) {
  const msg = (typeof err === 'string' ? err : err?.message ?? '').toLowerCase();
  if (!msg) return false;                       // no message: assume transient, keep the session
  return DEFINITIVE_AUTH_ERRORS.some(code => msg.includes(code));
}

// Share one in-flight call between concurrent callers. The wrapped function
// runs at most once at a time; everyone awaits the same promise and gets the
// same result. A new call after settlement starts a fresh flight.
export function singleFlight(fn) {
  let inFlight = null;
  return (...args) => {
    if (inFlight) return inFlight;
    const p = Promise.resolve().then(() => fn(...args));
    inFlight = p;
    const clear = () => { if (inFlight === p) inFlight = null; };
    p.then(clear, clear);                       // settles either way; never an unhandled rejection
    return p;
  };
}
